import type { CategoryNode } from '../api/types';
import { categoryNodesById, SYNTHETIC_CATEGORY_IDS } from './category-tree';
import { isInstitutionLevel } from './period-tree';

/**
 * Pure helpers for "Add a new category…" in the review panel (owner ask
 * 2026-10-12: a category the room's checklist did not think of, typically the
 * missing third level). The panel creates the category through the existing
 * folder import (POST /rooms/:id/tags/import with `parentTagId`, so it shares
 * its parent's tier, ADR-0010 §2) and files the document there through the
 * category PATCH, which records the person's decision (ADR-0020).
 */

/** The longest category label the import accepts (tags/import.ts MAX_NAME_LENGTH). */
export const MAX_CATEGORY_NAME_LENGTH = 200;

/** Trim and collapse inner whitespace: the name as it is saved. */
export function cleanCategoryName(name: string): string {
  return name.replace(/\s+/g, ' ').trim();
}

/** The comparison key for "is this the same category?": Unicode-normalized,
 * case-folded, curly quotes straightened, whitespace collapsed. */
export function normalizeCategoryName(name: string): string {
  return cleanCategoryName(
    name
      .normalize('NFKC')
      .replace(/[‘’‛′]/g, "'")
      .replace(/[“”‟″]/g, '"'),
  ).toLocaleLowerCase('en-US');
}

/** The existing child of `parentId` (null = a top-level category) whose name
 * matches `name` once both are normalized, or null. Only approved categories are in the tree, so this is the
 * set a person can file into. */
export function findSiblingByName(
  categories: readonly CategoryNode[],
  parentId: string | null,
  name: string,
): CategoryNode | null {
  const siblings =
    parentId === null ? categories : categoryNodesById(categories).get(parentId)?.children;
  if (!siblings) return null;
  const key = normalizeCategoryName(name);
  if (!key) return null;
  return (
    siblings.find(
      (c) => !SYNTHETIC_CATEGORY_IDS.has(c.id) && normalizeCategoryName(c.name) === key,
    ) ?? null
  );
}

/** Five or more digits in a row, or a long number written in groups
 * ("3714 496353 98431", "1234-5678-9012"): what an account or card number looks
 * like. A year ("2022") or a last 4 ("…8431") is fine. */
const ACCOUNT_NUMBER = /\d{5,}|\d(?:[ .-]?\d){8,}/;

/** A user-typed category name must never carry a full account number. Returns
 * the name with every such number cut to its last 4 digits (the suggestion), or
 * null when the name is fine. */
export function accountNumberSuggestion(name: string): string | null {
  if (!ACCOUNT_NUMBER.test(name)) return null;
  const global = new RegExp(ACCOUNT_NUMBER.source, 'g');
  return cleanCategoryName(name.replace(global, (run) => run.replace(/\D/g, '').slice(-4)));
}

export type CategoryNameProblem =
  | { kind: 'empty' }
  | { kind: 'too_long' }
  | { kind: 'slash' }
  | { kind: 'account_number'; suggestion: string };

/** Why `name` cannot be saved as a category, or null when it can. */
export function categoryNameProblem(name: string): CategoryNameProblem | null {
  const clean = cleanCategoryName(name);
  if (!clean) return { kind: 'empty' };
  if (clean.length > MAX_CATEGORY_NAME_LENGTH) return { kind: 'too_long' };
  // The panel shows a category as "A / B / C": a slash inside a name would read
  // as another level.
  if (clean.includes('/')) return { kind: 'slash' };
  const suggestion = accountNumberSuggestion(clean);
  if (suggestion !== null) return { kind: 'account_number', suggestion };
  return null;
}

/** The import's 200 body (tags/import.ts `ImportCategoryHierarchyResult`). */
export interface CategoryImportResponse {
  nodes: { id: string; name: string; created: boolean; children: unknown[] }[];
  createdCount: number;
  reusedCount: number;
}

/**
 * A path of category ids without its "[Institution]" level ("Bank not
 * identified"): that level is a heading, never a filing, and the server refuses
 * a category set that carries it ("An institution is not a place to file a
 * document") — so a card added under "Bank Docs › [Institution] › Credit Card
 * Statements" was created and then left unfiled. The new category sits below
 * the heading either way.
 */
export function withoutInstitutionLevel(
  categories: readonly CategoryNode[],
  ids: readonly string[],
): string[] {
  const nodes = categoryNodesById(categories);
  return ids.filter((id) => !isInstitutionLevel(nodes.get(id)?.name));
}

/**
 * The document's category set once it is filed in the new category: when the
 * new category sits under the deepest category the document is already filed
 * in, its other categories are kept (one level deeper, like a review round);
 * under any other parent the path from the top level down replaces them (like
 * "Choose a different branch").
 */
export function categoryIdsAfterAdd(input: {
  filedIds: readonly string[];
  deepestFiledId: string | null;
  parentPathIds: readonly string[];
  newId: string;
}): string[] {
  const { filedIds, deepestFiledId, parentPathIds, newId } = input;
  const base =
    deepestFiledId !== null && parentPathIds[parentPathIds.length - 1] === deepestFiledId
      ? filedIds
      : parentPathIds;
  return [...new Set([...base, newId])];
}

/** Thrown when the import handed back a category the room's tree does not list:
 * a same-named category the AI proposed and nobody approved yet. The document is
 * not filed into it (it would be filed somewhere nobody can see). */
export class PendingCategoryError extends Error {
  constructor() {
    super(
      'A category with this name is already waiting for review here. Accept or reject that AI suggestion first, or choose another name.',
    );
    this.name = 'PendingCategoryError';
  }
}
